import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { BASE_PX_PER_MM, uid } from "@/lib/card";
import EditorHeader from "@/components/editor/EditorHeader";
import Toolbox from "@/components/editor/Toolbox";
import CanvasStage from "@/components/editor/CanvasStage";
import Inspector from "@/components/editor/Inspector";
import CardSettings from "@/components/editor/CardSettings";

const isTyping = (e) => ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.target.isContentEditable;

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [side, setSide] = useState("front");
  const [sel, setSel] = useState(null);
  const [zoom, setZoom] = useState(1.25);
  const [snap, setSnap] = useState(true);
  const [guides, setGuides] = useState(true);
  const [smart, setSmart] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const tplRef = useRef(null);
  const past = useRef([]);
  const future = useRef([]);

  useEffect(() => {
    api.get(`/templates/${id}`).then((r) => { tplRef.current = r.data; setTpl(r.data); })
      .catch(() => { toast.error("Design not found"); nav("/"); });
  }, [id, nav]);

  const commit = useCallback((fn) => {
    const prev = tplRef.current;
    const next = fn(prev);
    past.current.push(prev);
    if (past.current.length > 80) past.current.shift();
    future.current = [];
    tplRef.current = next;
    setTpl(next);
    setDirty(true);
  }, []);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    future.current.push(tplRef.current);
    tplRef.current = past.current.pop();
    setTpl(tplRef.current);
    setDirty(true);
  }, []);
  const redo = useCallback(() => {
    if (!future.current.length) return;
    past.current.push(tplRef.current);
    tplRef.current = future.current.pop();
    setTpl(tplRef.current);
    setDirty(true);
  }, []);

  const updateEls = useCallback((fn) => commit((t) => ({ ...t, [side]: { ...t[side], elements: fn(t[side].elements) } })), [commit, side]);
  const updateEl = useCallback((eid, patch) => updateEls((els) => els.map((e) => (e.id === eid ? { ...e, ...patch } : e))), [updateEls]);
  const addEls = useCallback((arr) => { updateEls((els) => [...els, ...arr]); setSel(arr[arr.length - 1].id); }, [updateEls]);
  const removeEl = useCallback((eid) => { updateEls((els) => els.filter((e) => e.id !== eid)); setSel(null); }, [updateEls]);
  const duplicateEl = useCallback((eid) => {
    const el = tplRef.current[side].elements.find((e) => e.id === eid);
    if (el) addEls([{ ...el, id: uid(), x: el.x + 2, y: el.y + 2 }]);
  }, [addEls, side]);
  const moveLayer = useCallback((eid, dir) => updateEls((els) => {
    const i = els.findIndex((e) => e.id === eid);
    const arr = [...els];
    const [el] = arr.splice(i, 1);
    const to = dir === "top" ? arr.length : dir === "bottom" ? 0 : Math.max(0, Math.min(arr.length, i + (dir === "up" ? 1 : -1)));
    arr.splice(to, 0, el);
    return arr;
  }), [updateEls]);

  const save = useCallback(async () => {
    const t = tplRef.current;
    setSaving(true);
    try {
      await api.put(`/templates/${id}`, { name: t.name, category: t.category, width: t.width, height: t.height, front: t.front, back: t.back, double_sided: t.double_sided ?? true, public_verify: t.public_verify ?? t.category === "jewellery" });
      setDirty(false);
      toast.success("Design saved");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setSaving(false); }
  }, [id]);

  const goTo = async (path) => { if (!dirty || (await save())) nav(path); };

  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "s") { e.preventDefault(); save(); return; }
      if (isTyping(e)) return;
      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); return; }
      if (!sel) return;
      if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); duplicateEl(sel); return; }
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeEl(sel); return; }
      const step = e.shiftKey ? 2 : 0.5;
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (d) {
        e.preventDefault();
        const el = tplRef.current[side].elements.find((x) => x.id === sel);
        if (el && !el.locked) updateEl(sel, { x: +(el.x + d[0]).toFixed(2), y: +(el.y + d[1]).toFixed(2) });
      }
      if (e.key === "Escape") setSel(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel, side, save, undo, redo, duplicateEl, removeEl, updateEl]);

  if (!tpl) return <div className="h-screen grid place-items-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  const selected = tpl[side].elements.find((e) => e.id === sel) || null;
  const scale = BASE_PX_PER_MM * zoom;

  return (
    <div className="h-screen flex flex-col bg-slate-950 overflow-hidden">
      <EditorHeader tpl={tpl} side={side} setSide={(s) => { setSide(s); setSel(null); }} zoom={zoom} setZoom={setZoom}
        snap={snap} setSnap={setSnap} guides={guides} setGuides={setGuides} smart={smart} setSmart={setSmart} dirty={dirty} saving={saving} onSave={save}
        onUndo={undo} onRedo={redo} onRename={(name) => commit((t) => ({ ...t, name }))} goTo={goTo} />
      <div className="flex-1 flex min-h-0">
        <Toolbox tpl={tpl} side={side} sel={sel} setSel={setSel} addEls={addEls} updateEl={updateEl} moveLayer={moveLayer} removeEl={removeEl} />
        <CanvasStage tpl={tpl} side={side} scale={scale} sel={sel} setSel={setSel} updateEl={updateEl} snap={snap} guides={guides} smart={smart} />
        <aside className="w-80 shrink-0 bg-slate-900 border-l border-slate-800 overflow-y-auto" data-testid="properties-panel">
          {selected ? (
            <Inspector el={selected} tpl={tpl} onChange={(p) => updateEl(selected.id, p)} onDelete={() => removeEl(selected.id)}
              onDuplicate={() => duplicateEl(selected.id)} onLayer={(d) => moveLayer(selected.id, d)} />
          ) : (
            <CardSettings tpl={tpl} side={side} commit={commit} />
          )}
        </aside>
      </div>
    </div>
  );
}
